import clsx from 'clsx';
import { formatCurrency } from '../../lib/format';
import { groupLineItems, type InvoiceLineItem } from '../../lib/invoices';

interface InvoiceLineItemsProps {
  items: InvoiceLineItem[];
  totals: {
    subtotaal: number;
    discount: number;
    totalExclBtw: number;
    btw: number;
    totalInclBtw: number;
  };
}

function itemLabel(item: InvoiceLineItem): string {
  return item.customName || item.productName || 'Item';
}

function itemQtyLabel(item: InvoiceLineItem): string {
  if (item.quantity <= 0) return '';
  if (item.isAreaCalculation) return `${item.quantity.toFixed(2)} m²`;
  return `${item.quantity} stuks`;
}

function LineRow({
  item,
  variant,
}: {
  item: InvoiceLineItem;
  variant: 'primary' | 'package' | 'extra' | 'upsell' | 'orphan';
}) {
  const isIndented = variant === 'package' || variant === 'extra';
  const price = item.totalInclBtwWithExtras || item.totalInclBtw || 0;
  return (
    <div
      className={clsx(
        'px-6 py-3 flex items-center gap-4',
        isIndented && 'pl-12 bg-slate-50/60'
      )}
    >
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-0.5 flex-wrap">
          {isIndented && <span className="text-slate-300 text-xs">↳</span>}
          <span
            className={clsx(
              'truncate',
              isIndented ? 'text-sm text-slate-700' : 'text-sm font-medium text-slate-900'
            )}
          >
            {itemLabel(item)}
          </span>
          {variant === 'package' && (
            <span className="text-[10px] px-1.5 py-0.5 rounded font-medium bg-indigo-100 text-indigo-700">
              Pakket
            </span>
          )}
          {variant === 'upsell' && (
            <span className="text-[10px] px-1.5 py-0.5 rounded font-medium bg-purple-100 text-purple-700">
              Upsell
            </span>
          )}
          {!isIndented && item.itemNumber && (
            <span className="text-[11px] text-slate-400 font-mono">{item.itemNumber}</span>
          )}
        </div>
        <div className="flex items-center gap-3 text-xs text-slate-500">
          {itemQtyLabel(item) && <span>{itemQtyLabel(item)}</span>}
          {item.discount > 0 && (
            <span className="text-emerald-600">-{item.discount.toFixed(0)}% korting</span>
          )}
        </div>
      </div>
      <div
        className={clsx(
          'flex-shrink-0',
          isIndented ? 'text-sm text-slate-600' : 'text-sm font-medium text-slate-900'
        )}
      >
        {formatCurrency(price)}
      </div>
    </div>
  );
}

export function InvoiceLineItems({ items, totals }: InvoiceLineItemsProps) {
  const { primaries, upsells, orphans } = groupLineItems(items);
  const hasAny = primaries.length > 0 || upsells.length > 0 || orphans.length > 0;

  return (
    <div className="bg-white rounded-xl shadow-sm border border-slate-200">
      <div className="px-6 py-3 border-b border-slate-100">
        <h2 className="text-sm font-semibold text-slate-900">
          Regels ({items.length})
        </h2>
      </div>

      {!hasAny ? (
        <p className="px-6 py-6 text-sm text-slate-400 text-center">Geen factuurregels</p>
      ) : (
        <div className="divide-y divide-slate-100">
          {primaries.map((group) => (
            <div key={group.primary.id} className="py-1">
              <LineRow item={group.primary} variant="primary" />
              {group.packages.map((pkg) => (
                <LineRow key={pkg.id} item={pkg} variant="package" />
              ))}
              {group.extras.map((extra) => (
                <LineRow key={extra.id} item={extra} variant="extra" />
              ))}
            </div>
          ))}

          {upsells.length > 0 && (
            <div className="py-1">
              <div className="px-6 pt-3 pb-1 text-[10px] font-semibold uppercase tracking-wide text-slate-400">
                Upsells
              </div>
              {upsells.map((item) => (
                <LineRow key={item.id} item={item} variant="upsell" />
              ))}
            </div>
          )}

          {orphans.length > 0 && (
            <div className="py-1">
              <div className="px-6 pt-3 pb-1 text-[10px] font-semibold uppercase tracking-wide text-slate-400">
                Losse items
              </div>
              {orphans.map((item) => (
                <LineRow key={item.id} item={item} variant="orphan" />
              ))}
            </div>
          )}
        </div>
      )}

      <div className="px-6 py-4 border-t border-slate-100 bg-slate-50 rounded-b-xl space-y-1.5">
        {totals.discount > 0 && (
          <>
            <div className="flex justify-between text-xs text-slate-500">
              <span>Subtotaal</span>
              <span>{formatCurrency(totals.subtotaal)}</span>
            </div>
            <div className="flex justify-between text-xs text-emerald-600">
              <span>Korting</span>
              <span>-{formatCurrency(totals.discount)}</span>
            </div>
          </>
        )}
        <div className="flex justify-between text-xs text-slate-500">
          <span>Totaal excl. BTW</span>
          <span>{formatCurrency(totals.totalExclBtw)}</span>
        </div>
        <div className="flex justify-between text-xs text-slate-500">
          <span>BTW</span>
          <span>{formatCurrency(totals.btw)}</span>
        </div>
        <div className="flex justify-between text-sm font-semibold text-slate-900 pt-1 border-t border-slate-200">
          <span>Totaal incl. BTW</span>
          <span>{formatCurrency(totals.totalInclBtw)}</span>
        </div>
      </div>
    </div>
  );
}
